import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid, IconButton, Paper, TextField, Typography } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

export const readTelegramButtons = value => {
  if (Array.isArray(value)) return value;
  try { const rows = JSON.parse(value || "[]"); return Array.isArray(rows) ? rows : []; } catch { return []; }
};

export default function TelegramInlineCampaignButtons({ value, onChange, disabled }) {
  const [config, setConfig] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingBot, setEditingBot] = useState(false);
  const rows = readTelegramButtons(value);

  useEffect(() => {
    let active = true;
    api.get("/group-campaigns/telegram-inline-bot")
      .then(({ data }) => { if (active) setConfig(data); })
      .catch(err => { if (active) toastError(err); });
    return () => { active = false; };
  }, []);

  const saveBot = async (useStoreBot = false) => {
    setBusy(true);
    try {
      const { data } = await api.put("/group-campaigns/telegram-inline-bot", useStoreBot ? { useStoreBot: true } : { token: token.trim() });
      setConfig(data);
      setToken("");
      setEditingBot(false);
      toast.success("Bot de campañas guardado y verificado");
    } catch (err) { toastError(err); } finally { setBusy(false); }
  };

  const updateButton = (rowIndex, buttonIndex, field, next) => onChange(rows.map((row, r) =>
    r === rowIndex ? row.map((button, b) => b === buttonIndex ? { ...button, [field]: next } : button) : row
  ));
  const removeButton = (rowIndex, buttonIndex) => onChange(rows.map((row, r) =>
    r === rowIndex ? row.filter((_, b) => b !== buttonIndex) : row
  ).filter(row => row.length));

  return <Paper variant="outlined" style={{ padding: 16, width: "100%" }}>
    <Typography variant="subtitle1">Botones de acción de Telegram</Typography>
    <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
      Para usar botones se requiere un bot de Telegram. Tu cuenta conectada publica el mensaje vía el bot inline;
      el bot no necesita pertenecer al grupo o canal. El destino debe permitir mensajes inline.
    </Typography>
    <Box mt={2}>
      {config?.configured ? <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
        <Typography variant="body2">Bot configurado para esta empresa: <strong>@{config.username}</strong>{config.mode === "store" ? " · Bot de tienda" : ""}</Typography>
        <Button size="small" disabled={disabled || busy} onClick={() => setEditingBot(!editingBot)}>Cambiar bot</Button>
        <Button size="small" disabled={disabled || busy} onClick={() => saveBot()}>Verificar conexión</Button>
      </Box> : <Typography variant="body2">Configura un bot para habilitar los botones. Los envíos sin botones usan la cuenta conectada.</Typography>}
      {(!config?.configured || editingBot) && <>
        {config?.storeBotAvailable && <Box mt={1}>
          <Button variant="contained" color="primary" disabled={disabled || busy} onClick={() => saveBot(true)}>
            Usar el bot de mi tienda{config.storeBotUsername ? ` (@${config.storeBotUsername})` : ""}
          </Button>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Conserva la conexión, los menús y el diseño de tu bot. Activa /setinline en @BotFather si aún no está habilitado.
          </Typography>
        </Box>}
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          También puedes usar un bot dedicado: créalo con /newbot en @BotFather y activa /setinline.
          No uses un bot conectado a otras integraciones.
        </Typography>
        <Box display="flex" alignItems="center" flexWrap="wrap" mt={1} style={{ gap: 8 }}>
          <TextField id="telegram-campaign-bot-token" label="Token del bot de Telegram" type="password" value={token}
            onChange={e => setToken(e.target.value)} variant="outlined" size="small"
            autoComplete="new-password" disabled={disabled || busy} style={{ flex: "1 1 220px", minWidth: 0 }}
            helperText="Se guarda protegido. No se muestra el token guardado." />
          <Button variant="outlined" color="primary" disabled={disabled || busy || !token.trim()}
            onClick={() => saveBot()}>{busy ? <CircularProgress size={18} /> : "Guardar y verificar bot"}</Button>
        </Box>
      </>}
      {config?.configured && <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
        Para adjuntar archivos, abre @{config.username} desde la cuenta conectada y pulsa Iniciar.
        El archivo se prepara en ese chat privado y el mensaje temporal se elimina.
      </Typography>}
    </Box>
    {rows.map((row, rowIndex) => <Box key={rowIndex} mt={2}>
      <Typography variant="caption" color="textSecondary">Fila {rowIndex + 1}</Typography>
      {row.map((button, buttonIndex) => <Grid container spacing={1} alignItems="center" key={buttonIndex}>
        <Grid item xs={4}><TextField fullWidth id={`telegram-button-text-${rowIndex}-${buttonIndex}`} label={`Texto del botón ${rowIndex + 1}.${buttonIndex + 1}`}
          value={button.text} variant="outlined" size="small" margin="dense" disabled={disabled}
          onChange={e => updateButton(rowIndex, buttonIndex, "text", e.target.value)} /></Grid>
        <Grid item xs={7}><TextField fullWidth id={`telegram-button-url-${rowIndex}-${buttonIndex}`} label={`Enlace del botón ${rowIndex + 1}.${buttonIndex + 1}`}
          value={button.url} placeholder="https://…" variant="outlined" size="small" margin="dense" disabled={disabled}
          onChange={e => updateButton(rowIndex, buttonIndex, "url", e.target.value)} /></Grid>
        <Grid item xs={1}><IconButton size="small" aria-label={`Eliminar botón ${rowIndex + 1}.${buttonIndex + 1}`}
          disabled={disabled} onClick={() => removeButton(rowIndex, buttonIndex)}><DeleteIcon fontSize="small" /></IconButton></Grid>
      </Grid>)}
      <Button size="small" disabled={disabled || row.length >= 8}
        onClick={() => onChange(rows.map((r, i) => i === rowIndex ? [...r, { text: "", url: "" }] : r))}>Añadir botón en esta fila</Button>
    </Box>)}
    <Box mt={2}>
      {config?.configured && <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 8 }}>
        {[["store", "Tienda web"], ["whatsapp", "WhatsApp"], ["telegram", "Bot de Telegram"]].map(([key, text]) => <Button key={key}
          size="small" disabled={disabled || !config.links?.[key] || rows.length >= 20}
          onClick={() => onChange([...rows, [{ text, url: config.links[key] }]])}>Añadir {text}</Button>)}
      </Box>}
      <Button variant="outlined" size="small" color="primary" disabled={disabled || !config?.configured || rows.length >= 20}
        onClick={() => onChange([...rows, [{ text: "", url: "" }]])}>Añadir fila de botones</Button>
      <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
        Usa enlaces completos a tu tienda, WhatsApp, Telegram o página de pago. Los mismos botones acompañan cada mensaje y archivo de la campaña.
      </Typography>
    </Box>
  </Paper>;
}
